import { Award, Clock, Users, CheckCircle2, Sparkles, ShieldCheck, Briefcase, Target, Zap } from "lucide-react";
import { Badge } from "@/components/ui/badge";
import { useLanguage } from "@/hooks/use-language";
import { getTranslation } from "@/lib/translations";

// Changed the order of values to move "Profesjonalizm i doświadczenie" to the middle
const values = [
  {
    titleKey: "individualApproach" as const,
    descriptionKey: "individualApproachDescription" as const,
    icon: Users,
    benefits: [
      "dedicatedCustomerService" as const,
      "flexibleSolutions" as const,
      "offerCustomization" as const,
      "constantCommunicationAndFeedback" as const
    ]
  },
  {
    titleKey: "professionalismAndExperience" as const,
    descriptionKey: "professionalismDescription" as const,
    icon: Briefcase,
    benefits: [
      "knowledgeBackedByPractice" as const,
      "knowledgeOfCurrentRegulations" as const,
      "continuousSkillImprovement" as const,
      "cooperationWithIndustryExperts" as const
    ]
  },
  {
    titleKey: "operationalEfficiency" as const,
    descriptionKey: "operationalEfficiencyDescription" as const,
    icon: Zap,
    benefits: [
      "fastOrderExecution" as const,
      "meetingDeadlines" as const,
      "processOptimization" as const,
      "formalitiesMinimization" as const
    ]
  }
];

const stats = [
  { value: "7+", labelKey: "yearsOfExperience" as const },
  { value: "2500+", labelKey: "satisfiedClients" as const },
  { value: "99.7%", labelKey: "successRate" as const },
];

export default function ValuesSection() {
  const { language } = useLanguage();

  return (
    <>
      {/* Sekcja 1: Poznaj nasze atuty */}
      <section className="py-12 sm:py-14 md:py-16 lg:py-20 bg-secondary/30">
        <div className="container px-4 sm:px-6">
          <div className="text-center mb-8 sm:mb-10 md:mb-12">
            <Badge variant="outline" className="mb-3 sm:mb-4 px-3 sm:px-4 py-1 text-xs sm:text-sm bg-primary/10 text-primary border-primary/20">{getTranslation("whyUs", language)}</Badge>
            <h2 className="text-2xl sm:text-3xl md:text-4xl font-bold mb-4 sm:mb-6 tracking-tight px-2 sm:px-0">{getTranslation("ourAdvantages", language).split(" ")[0]} <span className="text-gradient-midnight">{getTranslation("ourAdvantages", language).split(" ").slice(1).join(" ")}</span></h2>
            <p className="text-base sm:text-lg text-muted-foreground max-w-3xl mx-auto px-2 sm:px-4">
              {getTranslation("ourAdvantagesDescription", language)}
            </p>
          </div>

          {/* Statystyki */}
          <div className="grid grid-cols-1 sm:grid-cols-3 gap-4 sm:gap-6 mb-6 sm:mb-8 max-w-3xl mx-auto">
            {stats.map((stat, i) => (
              <div key={i} className="bg-card rounded-xl p-3 sm:p-4 text-center shadow-sm border border-border/60 hover:border-primary/20 transition-colors">
                <div className="text-2xl sm:text-3xl font-bold text-primary mb-0.5 sm:mb-1">{stat.value}</div>
                <div className="text-xs sm:text-sm text-muted-foreground">{getTranslation(stat.labelKey, language)}</div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Sekcja 2: Profesjonalizm i doświadczenie */}
      <section className="py-12 sm:py-16 md:py-20 lg:py-24 bg-background">
        <div className="container px-4 sm:px-6">
          <div className="text-center mb-8 sm:mb-10 md:mb-12">
            <Badge variant="outline" className="mb-3 sm:mb-4 px-3 sm:px-4 py-1 text-xs sm:text-sm bg-primary/10 text-primary border-primary/20">{getTranslation("ourValues", language)}</Badge>
            <h2 className="text-2xl sm:text-3xl md:text-4xl font-bold mb-4 sm:mb-6 tracking-tight px-2 sm:px-0">{getTranslation("professionalismAndExperience", language).split(" ")[0]} <span className="text-gradient-midnight">{getTranslation("professionalismAndExperience", language).split(" ").slice(1).join(" ")}</span></h2>
          </div>

          {/* Wartości firmy - wszystkie w jednym rzędzie na środku strony */}
          <div className="flex flex-col md:flex-row gap-6 sm:gap-8 max-w-6xl mx-auto">
            {values.map((value, index) => (
              <div 
                key={index}
                className="flex-1 animate-fade-in-up"
                style={{animationDelay: `${index * 100}ms`, order: index}}
              >
                <div className="bg-card rounded-xl p-4 sm:p-6 md:p-8 shadow-sm border border-border/60 hover:border-primary/20 transition-all duration-300 feature-card h-full">
                  <div className="flex items-center gap-3 sm:gap-4 mb-3 sm:mb-4">
                    <div className="bg-primary/10 p-3 sm:p-4 rounded-full">
                      <value.icon className="h-6 w-6 sm:h-7 sm:w-7 md:h-8 md:w-8 text-primary" />
                    </div>
                    <h3 className="text-xl sm:text-2xl font-bold">{getTranslation(value.titleKey, language)}</h3>
                  </div>
                  <p className="text-sm sm:text-base text-muted-foreground mb-4 sm:mb-6">{getTranslation(value.descriptionKey, language)}</p>
                  
                  <div className="grid grid-cols-1 gap-2 sm:gap-3">
                    {value.benefits.map((benefit, i) => (
                      <div key={i} className="flex items-center gap-2 sm:gap-3 group">
                        <div className="bg-primary/10 rounded-full p-1 group-hover:bg-primary/20 transition-colors">
                          <CheckCircle2 className="h-4 w-4 sm:h-5 sm:w-5 text-primary flex-shrink-0" />
                        </div>
                        <span className="text-sm sm:text-base">{getTranslation(benefit, language)}</span>
                      </div>
                    ))}
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>
    </>
  );
}